import {
  CreateHarmReportRequest,
  CreateInterventionReportRequest,
  CreateReportResponse,
  DeleteHarmReportsResponse,
  DeleteInterventionReportsResponse,
  GetPlayerHarmCountQueryParams,
  GetPlayerHarmCountResponse,
  GetPlayerInterventionCountQueryParams,
  GetPlayerInterventionCountResponse,
  GetReportsByFilterRequest,
  UpdateHarmReportRequest,
  UpdateHarmReportResponse,
  UpdateInterventionReportRequest,
  UpdateInterventionReportResponse,
} from '@inplay/models';
import { getEnvironmentVariable } from '../helper/config';
import axiosClient from '../utils/apiClient';
import apiClient from '../utils/apiClient';
import { isApiError } from '../utils/helper';
import { EnvVariables } from '../utils/helper.type';
import { GetHarmReportsResponse } from '../pages/harmReports/harmReports.type';
import { GetInterventionReportsResponse } from '../pages/interventionReports/interventionReports.type';

export class ReportService {
  private baseApi: string;

  constructor() {
    const baseUrl: string | undefined = axiosClient.defaults.baseURL;

    if (!baseUrl) {
      throw new Error('baseURL is not defined in axiosClient.');
    }

    this.baseApi = baseUrl;
  }

  public async createInterventionReport(
    payload: CreateInterventionReportRequest,
  ): Promise<CreateReportResponse> {
    try {
      console.log(`[ENTRY][ReportService][createInterventionReport]`, {
        ...payload,
      });

      const createInterventionReportApiResource: string =
        getEnvironmentVariable(
          EnvVariables.REACT_APP_INTERVENTION_REPORT_RESOURCE,
        );

      const createInterventionReportApiEndpoint: string = `${this.baseApi}${createInterventionReportApiResource}`;

      const createInterventionReportApiResponse: CreateReportResponse =
        await apiClient.post(createInterventionReportApiEndpoint, {
          ...payload,
        });

      console.log(`[EXIT][ReportService][createInterventionReport]`, {
        createInterventionReportApiResponse,
      });
      return createInterventionReportApiResponse;
    } catch (error: unknown) {
      console.error(`[ReportService][createInterventionReport][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async getPlayerInterventionCount(
    playerId: string,
    query: GetPlayerInterventionCountQueryParams,
  ): Promise<GetPlayerInterventionCountResponse> {
    try {
      console.log(`[ENTRY][ReportService][getPlayerInterventionCount]`);

      const getInterventionReportsApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_INTERVENTION_REPORT_RESOURCE,
      );

      const queryParams = new URLSearchParams({
        venueBranchId: query.venueBranchId,
        currentDate: query.currentDate,
      });

      const getInterventionReportsApiEndpoint: string = `${this.baseApi}${getInterventionReportsApiResource}/players/${playerId}/counts?${queryParams.toString()}`;
      const getInterventionReportsApiResponse: GetPlayerInterventionCountResponse =
        (await apiClient.get(getInterventionReportsApiEndpoint)).data;

      console.log(`[EXIT][ReportService][getPlayerInterventionCount]`, {
        getInterventionReportsApiResponse,
      });
      return getInterventionReportsApiResponse;
    } catch (error: unknown) {
      console.error(`[ReportService][getPlayerInterventionCount][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async createHarmReport(
    payload: CreateHarmReportRequest,
  ): Promise<CreateReportResponse> {
    try {
      console.log(`[ENTRY][ReportService][createHarmReport]`, {
        ...payload,
      });

      const createHarmReportApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_HARM_REPORT_RESOURCE,
      );

      const createHarmReportApiEndpoint: string = `${this.baseApi}${createHarmReportApiResource}`;

      const createHarmReportApiResponse: CreateReportResponse =
        await apiClient.post(createHarmReportApiEndpoint, {
          ...payload,
        });

      console.log(`[EXIT][ReportService][createHarmReport]`, {
        createHarmReportApiResponse,
      });
      return createHarmReportApiResponse;
    } catch (error: unknown) {
      console.error(`[ReportService][createHarmReport][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async getHarmReports(
    query: GetReportsByFilterRequest & { linked?: boolean },
  ): Promise<GetHarmReportsResponse> {
    try {
      console.log(`[ENTRY][ReportService][getHarmReports]`);

      const getHarmReportsApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_HARM_REPORT_RESOURCE,
      );
      const utcDate = new Date(
        Date.UTC(
          query.currentDate.getFullYear(),
          query.currentDate.getMonth(),
          query.currentDate.getDate(),
        ),
      );
      const queryParams = new URLSearchParams({
        venueBranchId: query.venueBranchId,
        currentDate: utcDate.toISOString(),
        ...(query.playerId && { playerId: query.playerId }),
        showCount: 'true',
        ...(query.linked !== undefined && { linked: String(query.linked) }),
        generateImages: 'true',
      });
      if (query.status) {
        for (const status of query.status) {
          queryParams.append('status', status);
        }
      }
      const getHarmReportsApiEndpoint: string = `${this.baseApi}${getHarmReportsApiResource}?${queryParams.toString()}`;
      const getHarmReportsApiResponse: GetHarmReportsResponse =
        await apiClient.get(getHarmReportsApiEndpoint);

      console.log(`[EXIT][ReportService][getHarmReports]`, {
        getHarmReportsApiResponse,
      });
      return getHarmReportsApiResponse;
    } catch (error: unknown) {
      console.error(`[ReportService][getHarmReports][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async getPlayerHarmCount(
    playerId: string,
    query: GetPlayerHarmCountQueryParams,
  ): Promise<GetPlayerHarmCountResponse> {
    try {
      console.log(`[ENTRY][ReportService][getPlayerInterventionCount]`);

      const getHarmReportsApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_HARM_REPORT_RESOURCE,
      );

      const queryParams = new URLSearchParams({
        venueBranchId: query.venueBranchId,
        currentDate: query.currentDate,
      });

      const getHarmReportsApiEndpoint: string = `${this.baseApi}${getHarmReportsApiResource}/players/${playerId}/counts?${queryParams.toString()}`;
      const getHarmReportsApiResponse: GetPlayerHarmCountResponse = (
        await apiClient.get(getHarmReportsApiEndpoint)
      ).data;

      console.log(`[EXIT][ReportService][getPlayerInterventionCount]`, {
        getHarmReportsApiResponse,
      });
      return getHarmReportsApiResponse;
    } catch (error: unknown) {
      console.error(`[ReportService][getPlayerInterventionCount][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async editHarmReport(
    request: UpdateHarmReportRequest,
  ): Promise<UpdateHarmReportResponse> {
    try {
      console.log(`[ENTRY][ReportService][editHarmReport]`, {
        ...request,
      });

      const updateHarmReportApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_HARM_REPORT_RESOURCE,
      );

      const updateHarmReportApiEndpoint: string = `${this.baseApi}${updateHarmReportApiResource}`;

      const updateHarmReportApiResponse: UpdateHarmReportResponse =
        await apiClient.patch(updateHarmReportApiEndpoint, {
          ...request,
        });

      console.log(`[EXIT][ReportService][editHarmReport]`, {
        updateHarmReportApiResponse,
      });
      return updateHarmReportApiResponse;
    } catch (error: unknown) {
      console.error(`[ReportService][editHarmReport][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async getInterventionReports(query: GetReportsByFilterRequest) {
    try {
      console.log(`[ENTRY][ReportService][getInterventionReports]`, { query });
      const getInterventionReportsApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_INTERVENTION_REPORT_RESOURCE,
      );
      const utcDate = new Date(
        Date.UTC(
          query.currentDate.getFullYear(),
          query.currentDate.getMonth(),
          query.currentDate.getDate(),
        ),
      );
      const queryParams = new URLSearchParams({
        venueBranchId: query.venueBranchId,
        currentDate: utcDate.toISOString(),
        ...(query.playerId && { playerId: query.playerId }),
        showCount: 'true',
        ...(query.linked !== undefined && { linked: String(query.linked) }),
        generateImages: 'true',
      });
      const getInterventionReportsApiEndpoint: string = `${this.baseApi}${getInterventionReportsApiResource}?${queryParams.toString()}`;
      const getInterventionReportsApiResponse: GetInterventionReportsResponse =
        await apiClient.get(getInterventionReportsApiEndpoint);

      console.log(`[EXIT][ReportService][getInterventionReports]`, {
        getInterventionReportsApiResponse,
      });
      return getInterventionReportsApiResponse;
    } catch (error: unknown) {
      console.error(`[ReportService][getInterventionReports][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async editInterventionReport(
    request: UpdateInterventionReportRequest,
  ): Promise<UpdateInterventionReportResponse> {
    try {
      console.log(`[ENTRY][ReportService][editInterventionReport]`, {
        ...request,
      });

      const updateInterventionReportApiResource: string =
        getEnvironmentVariable(
          EnvVariables.REACT_APP_INTERVENTION_REPORT_RESOURCE,
        );

      const updateInterventionReportApiEndpoint: string = `${this.baseApi}${updateInterventionReportApiResource}`;

      const updateInterventionReportApiResponse: UpdateInterventionReportResponse =
        await apiClient.patch(updateInterventionReportApiEndpoint, {
          ...request,
        });

      console.log(`[EXIT][ReportService][editInterventionReport]`, {
        updateInterventionReportApiResponse,
      });
      return updateInterventionReportApiResponse;
    } catch (error: unknown) {
      console.error(`[ReportService][editInterventionReport][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async deleteHarmReports(
    originalReportIds: string[],
  ): Promise<DeleteHarmReportsResponse> {
    try {
      console.log(`[ENTRY][ReportService][deleteHarmReports]`, {
        originalReportIds,
      });

      const harmReportsApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_HARM_REPORT_RESOURCE,
      );
      const deleteHarmReportsApiEndpoint: string = `${this.baseApi}${harmReportsApiResource}`;

      const deleteHarmReportsResponse: DeleteHarmReportsResponse =
        await apiClient.delete(deleteHarmReportsApiEndpoint, {
          data: { originalReportIds },
        });

      console.log(`[EXIT][ReportService][deleteHarmReports]`, {
        deleteHarmReportsResponse,
      });
      return deleteHarmReportsResponse;
    } catch (error: unknown) {
      console.error(`[ReportService][deleteHarmReports][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }

  public async deleteInterventionReports(
    originalReportIds: string[],
  ): Promise<DeleteInterventionReportsResponse> {
    try {
      console.log(`[ENTRY][ReportService][deleteInterventionReports]`, {
        originalReportIds,
      });

      const interventionReportsApiResource: string = getEnvironmentVariable(
        EnvVariables.REACT_APP_INTERVENTION_REPORT_RESOURCE,
      );
      const deleteInterventionReportsApiEndpoint: string = `${this.baseApi}${interventionReportsApiResource}`;

      const deleteInterventionReportsResponse: DeleteInterventionReportsResponse =
        await apiClient.delete(deleteInterventionReportsApiEndpoint, {
          data: { originalReportIds },
        });

      console.log(`[EXIT][ReportService][deleteInterventionReports]`, {
        deleteInterventionReportsResponse,
      });
      return deleteInterventionReportsResponse;
    } catch (error: unknown) {
      console.error(`[ReportService][deleteInterventionReports][ERROR]`, {
        error,
      });
      if (isApiError(error)) {
        throw error;
      }
      throw error;
    }
  }
}
